import React, { useEffect, ReactNode } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAzureAuth } from '../../../hooks/useAzureAuth';
import { useAuthState, useAuthDispatch } from '../../../contexts/auth-context';
import { login } from '../../../contexts/actions/authActions';

interface AuthProviderProps {
  children: ReactNode;
}

const AuthCallbackProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { handleAuthCallback, authState } = useAzureAuth();
  const { auth } = useAuthState();
  const dispatch = useAuthDispatch();

  // Safely access router hooks with error handling
  let history: any = null;
  let location: any = null;

  try {
    history = useHistory();
    location = useLocation();
  } catch (error) {
    console.warn(
      'Router hooks not available, skipping auth callback processing'
    );
  }

  // Handle auth callback processing
  useEffect(() => {
    if (!location || !history) {
      return;
    }

    const processAuthCallback = async () => {
      // Check if we're on the auth callback page
      if (location.pathname === '/auth/callback') {
        try {
          const authResult = await handleAuthCallback();

          if (authResult && authResult.accessToken) {
            // Call the existing login action with Azure token
            await login({ azureToken: authResult.accessToken })(dispatch);

            // Redirect to the intended destination or home
            const intendedPath =
              sessionStorage.getItem('auth_redirect_path') || '/';
            sessionStorage.removeItem('auth_redirect_path');

            history.replace(intendedPath);
          } else {
            // If no auth result, redirect to login
            history.replace('/login');
          }
        } catch (error) {
          console.error('Auth callback processing failed:', error);
          // Redirect to login on error
          history.push('/login');
        }
      }
    };

    processAuthCallback();
  }, [location, handleAuthCallback, dispatch, history]);

  // Auto-login flow
  useEffect(() => {
    // Skip if already authenticated or on specific paths
    if (
      !location ||
      !history ||
      auth.isAuthenticated ||
      authState.isAuthenticated ||
      location.pathname === '/login' ||
      location.pathname === '/auth/callback'
    ) {
      return;
    }

    // Store the current path for redirect after login if not already on the login page
    if (!sessionStorage.getItem('auth_redirect_path')) {
      sessionStorage.setItem(
        'auth_redirect_path',
        location.pathname + location.search
      );
    }
  }, [location, history, auth.isAuthenticated, authState.isAuthenticated]);

  return <>{children}</>;
};

export default AuthCallbackProvider;
